"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BiMenu } from "react-icons/bi";
import { FaUser } from "react-icons/fa";
import { HiArrowLeft, HiBell, HiChevronRight } from "react-icons/hi2";
import { RiMessage3Line } from "react-icons/ri";
import { usePathname, useSearchParams } from "next/navigation";
import { CommandBar } from "@/components/command-bar";
import PatientStatusPanel from "@/components/custom-calendar/patient-status/patient-status-panel";
import { DeskPhoneCallIndicator } from "@/components/desk-phone-screen-pop/desk-phone-call-indicator";
import MissingDataPage from "@/components/missing-data-page";
import { NotificationsPanel } from "@/components/notifications/notifications-panel";
import { ProgressBarLink } from "@/components/progress-bar";
import RightSidebar from "@/components/right-sidebar";
import { TasksPanel } from "@/components/tasks/tasks-panel";
import { TeamChatPanel } from "@/components/team-chat/team-chat-panel";
import { WhisperHeaderButton } from "@/components/team-chat/whisper-header-button";
import { Button } from "@/components/ui";
import AIBadge from "@/components/ui/ai-badge";
import { ButtonProps } from "@/components/ui/button";
import { DrawerPortalContainerContext } from "@/components/ui/drawer";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SidebarProvider } from "@/components/ui/sidebar";
import Tooltip from "@/components/ui/tooltip";
import { type SiteMapItem, extractRouteParams, findNavSection, getBreadcrumbTailStart, getRelativePath, isEnterpriseRoute, replaceRouteParams } from "@/config/sitemap";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { findPageName, useBackRoute } from "@/hooks/use-back-route";
import { useEnhancedSitemapPath } from "@/hooks/use-enhanced-sitemap-path";
import { useLocations } from "@/hooks/use-locations";
import { useMenuItems } from "@/hooks/use-menu-items";
import { useModule } from "@/hooks/use-module";
import { useSidebarEnvironment } from "@/hooks/use-sidebar-environment";
import { PERMISSIONS, hasAnyPermission } from "@/lib/permissions";
import { clickableText, cx } from "@/lib/tailwind-utils";
import { Module } from "@/types/tenant";
import { AppSidebar } from "./app-sidebar";
import { ClockInButton } from "./clock-in-button";
import ProfileButton from "./profile-button";
import { AssistantSidebarCommand, assistantSidebarDefinition, selectAssistantPanels, selectPageAssistantDefinitions } from "@/features/sidebar/assistant-sidebar";
import { useCombinedNotificationCount } from "@/query/dosespot/dosespot-notifications";
import { useTeamUnreadCounts } from "@/query/team-chat";
import { useGlobalActionButtonStore } from "@/stores/global-action-button-store";
import { useNavigationSidebarOpenStore } from "@/stores/navigation-sidebar-store";
import { useNotificationsDrawerStore } from "@/stores/notifications-drawer-store";
import { usePatientStatusDrawerStore } from "@/stores/patient-status-drawer-store";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
import { useTasksDrawerStore } from "@/stores/tasks-drawer-store";
import { useTeamChatDrawerStore } from "@/stores/team-chat-store";
import { useChatTipTapEditorHeightStore } from "@/stores/tiptap-editor-height-store";
export { findPageName };
function AskDecodaHeaderButton() {
  const $ = _c(13);
  const pathname = usePathname();
  const hasAskDecoda = useModule("ASK_DECODA");
  const {
    currentUser
  } = useUser();
  const canUseAi = hasAnyPermission(currentUser, [PERMISSIONS.AI_USE, PERMISSIONS.AI_AGENT_USE, PERMISSIONS.AI_ANALYTICS_USE]);
  const state = useRightSidebarStore();
  let hasPageTabs;
  let t0;
  if ($[0] !== state) {
    const panels = selectAssistantPanels(state);
    hasPageTabs = selectPageAssistantDefinitions(state).length > 0;
    t0 = panels.some(panel => panel.id === state.activeKey);
    $[0] = state;
    $[1] = hasPageTabs;
    $[2] = t0;
  } else {
    hasPageTabs = $[1];
    t0 = $[2];
  }
  const isAiTabVisible = t0;
  if (pathname.includes("/ask-decoda")) {
    return null;
  }
  if (!canUseAi) {
    return null;
  }
  if (!hasAskDecoda && !hasPageTabs) {
    return null;
  }
  let t1;
  if ($[3] !== isAiTabVisible || $[4] !== state) {
    t1 = () => {
      state.open(assistantSidebarDefinition, {
        command: isAiTabVisible ? AssistantSidebarCommand.CLOSE_GROUP : AssistantSidebarCommand.OPEN
      });
    };
    $[3] = isAiTabVisible;
    $[4] = state;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleClick = t1;
  const t2 = isAiTabVisible ? "bg-primary/10 text-primary" : "text-foreground-secondary hover:bg-hover hover:text-foreground";
  let t3;
  if ($[6] !== t2) {
    t3 = cx("mr-1 hidden items-center gap-1.5 rounded-lg px-2 py-1 text-caption-strong transition-colors md:flex", t2);
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <AIBadge size={14} className="mr-0" />;
    t5 = <span>Ask Decoda</span>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  let t6;
  if ($[10] !== handleClick || $[11] !== t3) {
    t6 = <Tooltip content="Ask Decoda" side="bottom" triggerAsChild={true}><button onClick={handleClick} className={t3}>{t4}{t5}</button></Tooltip>;
    $[10] = handleClick;
    $[11] = t3;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}

/**
 * The crumb trail, laid out to fit whatever width the bar leaves it. As room
 * runs out, section crumbs drop out whole, outermost first, then the last crumb
 * truncates. The rest of the tail (e.g. a note's patient) never shrinks; it is
 * clipped only when it alone is wider than the bar.
 */
function BreadcrumbTrail(t0) {
  const $ = _c(24);
  const {
    path,
    currentRoute
  } = t0;
  if (path.length === 0) {
    return null;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== currentRoute || $[1] !== path) {
    const tailStart = getBreadcrumbTailStart(path);
    let t6;
    if ($[7] !== currentRoute || $[8] !== path.length) {
      t6 = (item, index, className) => {
        const isLast = index === path.length - 1;
        const isLink = !!item.route && path.length > 1 && !isLast;
        return <div key={`breadcrumb-${index}-${item.route ?? ""}`} className={cx("flex h-6 items-center", className)}>{isLink ? <ProgressBarLink href={item.route?.includes(":") ? replaceRouteParams(item.route, extractRouteParams(item.route, currentRoute ?? "")) : item.route} className={cx("flex min-w-0 items-center", clickableText)}><span className="truncate">{item.name}</span></ProgressBarLink> : <span className={cx("cursor-default truncate text-caption text-foreground-secondary md:text-body", isLast && !item.breadcrumbMuted && "font-semibold text-foreground")}>{item.name}{item.breadcrumbMeta && <span className="ml-2 font-normal text-foreground-muted">{item.breadcrumbMeta}</span>}</span>}{!isLast && <HiChevronRight className="mx-1 size-3 shrink-0 text-foreground-muted md:mx-1.5 md:size-4" />}</div>;
      };
      $[7] = currentRoute;
      $[8] = path.length;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    const renderCrumb = t6;
    t3 = "Breadcrumb";
    t4 = "flex h-6 min-w-0 flex-wrap content-end overflow-hidden";
    let t7;
    if ($[10] !== renderCrumb) {
      t7 = (item_0, index_0) => renderCrumb(item_0, index_0, "shrink-0");
      $[10] = renderCrumb;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    t5 = path.slice(0, tailStart).map(t7);
    t1 = "flex min-w-0 max-w-full shrink-0 items-center";
    let t8;
    if ($[12] !== path.length || $[13] !== renderCrumb || $[14] !== tailStart) {
      t8 = (item_1, i) => {
        const index_1 = tailStart + i;
        return renderCrumb(item_1, index_1, index_1 === path.length - 1 ? "min-w-0" : "shrink-0");
      };
      $[12] = path.length;
      $[13] = renderCrumb;
      $[14] = tailStart;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    t2 = path.slice(tailStart).map(t8);
    $[0] = currentRoute;
    $[1] = path;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[16] !== t1 || $[17] !== t2) {
    t6 = <div className={t1}>{t2}</div>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t3 || $[20] !== t4 || $[21] !== t5 || $[22] !== t6) {
    t7 = <nav aria-label={t3} className={t4}>{t5}{t6}</nav>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}
export function SidebarLayout(t0) {
  const $ = _c(205);
  const {
    children
  } = t0;
  useSidebarEnvironment();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    currentUser
  } = useUser();
  const {
    locations
  } = useLocations();
  const {
    tenant
  } = useTenant();
  const {
    isMobile
  } = useIsMobile();
  const {
    isCollapsed: isSidebarCollapsed,
    setIsCollapsed: setIsSidebarCollapsed,
    hoverOpen: isHoverSidebarOpen,
    setHoverOpen: setIsHoverSidebarOpen,
    expandSectionsForRoute
  } = useNavigationSidebarOpenStore();
  const unreadCount = useCombinedNotificationCount();
  const [mobileNotificationsOpen, setMobileNotificationsOpen] = useState(false);
  let t1;
  if ($[0] !== tenant?.enabledModules) {
    t1 = tenant?.enabledModules?.includes("WHISPER") ?? false;
    $[0] = tenant?.enabledModules;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isWhisperEnabled = t1;
  let t2;
  let t3;
  if ($[2] !== isWhisperEnabled) {
    t2 = () => {
      if (!isWhisperEnabled) {
        const {
          isOpen,
          isLocked
        } = useTeamChatDrawerStore.getState();
        if (isOpen || isLocked) {
          useTeamChatDrawerStore.getState().closeDrawer();
        }
      }
    };
    t3 = [isWhisperEnabled];
    $[2] = isWhisperEnabled;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const tenantIdentity = tenant?.subDomain ?? "";
  const prevTenantIdentityRef = useRef(tenantIdentity);
  let t4;
  let t5;
  if ($[5] !== tenantIdentity) {
    t4 = () => {
      if (prevTenantIdentityRef.current === tenantIdentity) {
        return;
      }
      prevTenantIdentityRef.current = tenantIdentity;
      const store = useTeamChatDrawerStore.getState();
      store.setActiveConversation(null);
      store.closeDrawer();
    };
    t5 = [tenantIdentity];
    $[5] = tenantIdentity;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  const {
    data: teamUnreadCounts
  } = useTeamUnreadCounts(isWhisperEnabled);
  let t6;
  if ($[8] !== teamUnreadCounts) {
    t6 = Object.values(teamUnreadCounts ?? {}).reduce(_temp, 0);
    $[8] = teamUnreadCounts;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const totalTeamUnread = t6;
  const {
    config: globalActionConfig
  } = useGlobalActionButtonStore();
  const {
    height: editorHeight
  } = useChatTipTapEditorHeightStore();
  const {
    isOpen: isNotificationsPanelOpen,
    isLocked: isNotificationsPanelLocked,
    closeDrawer: closeNotificationsPanel
  } = useNotificationsDrawerStore();
  const {
    isOpen: isPatientStatusPanelOpen,
    isLocked: isPatientStatusPanelLocked,
    closeDrawer: closePatientStatusPanel
  } = usePatientStatusDrawerStore();
  const {
    isOpen: isTasksPanelOpen,
    isLocked: isTasksPanelLocked,
    closeDrawer: closeTasksPanel
  } = useTasksDrawerStore();
  const {
    isOpen: isWhisperPanelOpen,
    isLocked: isWhisperPanelLocked,
    openDrawer: openWhisperPanel,
    closeDrawer: closeWhisperPanel
  } = useTeamChatDrawerStore();
  const notificationsPanelRef = useRef(null);
  const patientStatusPanelRef = useRef(null);
  const tasksPanelRef = useRef(null);
  const whisperPanelRef = useRef(null);
  const sidebarRef = useRef(null);
  const [mainContentElement, setMainContentElement] = useState(null);
  let t7;
  if ($[10] !== closeNotificationsPanel || $[11] !== closePatientStatusPanel || $[12] !== closeTasksPanel || $[13] !== closeWhisperPanel || $[14] !== isNotificationsPanelLocked || $[15] !== isNotificationsPanelOpen || $[16] !== isPatientStatusPanelLocked || $[17] !== isPatientStatusPanelOpen || $[18] !== isTasksPanelLocked || $[19] !== isTasksPanelOpen || $[20] !== isWhisperPanelLocked || $[21] !== isWhisperPanelOpen) {
    t7 = {
      isNotificationsPanelOpen,
      isNotificationsPanelLocked,
      isPatientStatusPanelOpen,
      isPatientStatusPanelLocked,
      isTasksPanelOpen,
      isTasksPanelLocked,
      isWhisperPanelOpen,
      isWhisperPanelLocked,
      closeNotificationsPanel,
      closePatientStatusPanel,
      closeTasksPanel,
      closeWhisperPanel
    };
    $[10] = closeNotificationsPanel;
    $[11] = closePatientStatusPanel;
    $[12] = closeTasksPanel;
    $[13] = closeWhisperPanel;
    $[14] = isNotificationsPanelLocked;
    $[15] = isNotificationsPanelOpen;
    $[16] = isPatientStatusPanelLocked;
    $[17] = isPatientStatusPanelOpen;
    $[18] = isTasksPanelLocked;
    $[19] = isTasksPanelOpen;
    $[20] = isWhisperPanelLocked;
    $[21] = isWhisperPanelOpen;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const stateRef = useRef(t7);
  let t8;
  let t9;
  if ($[23] !== closeNotificationsPanel || $[24] !== closePatientStatusPanel || $[25] !== closeTasksPanel || $[26] !== closeWhisperPanel || $[27] !== isNotificationsPanelLocked || $[28] !== isNotificationsPanelOpen || $[29] !== isPatientStatusPanelLocked || $[30] !== isPatientStatusPanelOpen || $[31] !== isTasksPanelLocked || $[32] !== isTasksPanelOpen || $[33] !== isWhisperPanelLocked || $[34] !== isWhisperPanelOpen) {
    t8 = () => {
      stateRef.current = {
        isNotificationsPanelOpen,
        isNotificationsPanelLocked,
        isPatientStatusPanelOpen,
        isPatientStatusPanelLocked,
        isTasksPanelOpen,
        isTasksPanelLocked,
        isWhisperPanelOpen,
        isWhisperPanelLocked,
        closeNotificationsPanel,
        closePatientStatusPanel,
        closeTasksPanel,
        closeWhisperPanel
      };
    };
    t9 = [isNotificationsPanelOpen, isNotificationsPanelLocked, isPatientStatusPanelOpen, isPatientStatusPanelLocked, isTasksPanelOpen, isTasksPanelLocked, isWhisperPanelOpen, isWhisperPanelLocked, closeNotificationsPanel, closePatientStatusPanel, closeTasksPanel, closeWhisperPanel];
    $[23] = closeNotificationsPanel;
    $[24] = closePatientStatusPanel;
    $[25] = closeTasksPanel;
    $[26] = closeWhisperPanel;
    $[27] = isNotificationsPanelLocked;
    $[28] = isNotificationsPanelOpen;
    $[29] = isPatientStatusPanelLocked;
    $[30] = isPatientStatusPanelOpen;
    $[31] = isTasksPanelLocked;
    $[32] = isTasksPanelOpen;
    $[33] = isWhisperPanelLocked;
    $[34] = isWhisperPanelOpen;
    $[35] = t8;
    $[36] = t9;
  } else {
    t8 = $[35];
    t9 = $[36];
  }
  useEffect(t8, t9);
  let t10;
  if ($[37] !== isMobile) {
    t10 = event => {
      if (isMobile) {
        return;
      }
      const target = event.target as Node;
      const state = stateRef.current;
      const isInsideRadixPopover = target instanceof Element && target.closest("[data-radix-popper-content-wrapper]");
      const isInsideRadixDialog = target instanceof Element && target.closest("[role='dialog']");
      const isInsideDrawerPortalRoot = target instanceof Element && target.closest("[data-drawer-portal-root]");
      const isInsideTippy = target instanceof Element && target.closest(".tippy-box");
      const isInsideRightSidebar = target instanceof Element && target.closest("[data-right-sidebar]");
      const isInsideBulkActionsBar = target instanceof Element && target.closest("[data-bulk-actions-bar]");
      const isClickOutsidePanel = panelRef => {
        if (!panelRef.current || !sidebarRef.current) {
          return false;
        }
        if (panelRef.current.contains(target)) {
          return false;
        }
        if (sidebarRef.current.contains(target)) {
          return false;
        }
        if (isInsideRadixDialog) {
          return false;
        }
        if (isInsideDrawerPortalRoot) {
          return false;
        }
        if (isInsideTippy) {
          return false;
        }
        if (isInsideRightSidebar) {
          return false;
        }
        if (isInsideBulkActionsBar) {
          return false;
        }
        if (isInsideRadixPopover) {
          const hasTriggerInPanel = !!panelRef.current.querySelector("[aria-expanded=\"true\"]");
          const hasTriggerInDialog = Array.from(document.querySelectorAll("[role=\"dialog\"]")).some(_temp2);
          if (hasTriggerInPanel || hasTriggerInDialog) {
            return false;
          }
        }
        return true;
      };
      const allowCloseDespiteLock = _temp3;
      if (state.isNotificationsPanelOpen && allowCloseDespiteLock(state.isNotificationsPanelLocked) && isClickOutsidePanel(notificationsPanelRef)) {
        state.closeNotificationsPanel();
      }
      if (state.isPatientStatusPanelOpen && allowCloseDespiteLock(state.isPatientStatusPanelLocked) && isClickOutsidePanel(patientStatusPanelRef)) {
        state.closePatientStatusPanel();
      }
      if (state.isTasksPanelOpen && allowCloseDespiteLock(state.isTasksPanelLocked) && isClickOutsidePanel(tasksPanelRef)) {
        state.closeTasksPanel();
      }
      if (state.isWhisperPanelOpen && allowCloseDespiteLock(state.isWhisperPanelLocked)) {
        const isWhisperTrigger = target instanceof Element && target.closest("[data-whisper-trigger]");
        if (!isWhisperTrigger && isClickOutsidePanel(whisperPanelRef)) {
          state.closeWhisperPanel();
        }
      }
    };
    $[37] = isMobile;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  const handleClickOutside = t10;
  let t11;
  let t12;
  if ($[39] !== handleClickOutside) {
    t11 = () => {
      document.addEventListener("mousedown", handleClickOutside, true);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside, true);
      };
    };
    t12 = [handleClickOutside];
    $[39] = handleClickOutside;
    $[40] = t11;
    $[41] = t12;
  } else {
    t11 = $[40];
    t12 = $[41];
  }
  useEffect(t11, t12);
  let baseRoute;
  let t13;
  if ($[42] !== pathname || $[43] !== tenant) {
    baseRoute = pathname.substring(pathname.indexOf("/", pathname.indexOf("/") + 1));
    const onEnterpriseRoute = tenant?.kind === "MASTER" && isEnterpriseRoute(baseRoute, tenant.enabledModules);
    t13 = onEnterpriseRoute ? undefined : findNavSection(baseRoute);
    $[42] = pathname;
    $[43] = tenant;
    $[44] = baseRoute;
    $[45] = t13;
  } else {
    baseRoute = $[44];
    t13 = $[45];
  }
  const navSection = t13;
  const isSubNavMode = Boolean(navSection);
  let t14;
  if ($[46] !== searchParams) {
    t14 = searchParams.get("section");
    $[46] = searchParams;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  const section = t14;
  const currentRoute = isSubNavMode ? `${baseRoute}${navSection?.sectionParamRows && section ? `?section=${section}` : ""}` : baseRoute || tenant?.homePage;
  const breadcrumbPath = useEnhancedSitemapPath(currentRoute ?? "");
  let t15;
  if ($[48] !== currentRoute || $[49] !== navSection) {
    t15 = {
      navSection,
      currentRoute
    };
    $[48] = currentRoute;
    $[49] = navSection;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  const menuItems = useMenuItems(t15);
  const {
    getBackRouteInfo,
    handleBack
  } = useBackRoute();
  tenant?.enabledModules;
  let t16;
  if ($[51] !== currentRoute || $[52] !== tenant?.enabledModules) {
    bb0: {
      const path = getRelativePath(currentRoute ?? "");
      const enabled = tenant?.enabledModules;
      for (let i = path.length - 1; i >= 0; i--) {
        const item = path[i];
        if (item.moduleName) {
          t16 = item.moduleName;
          break bb0;
        }
        if (item.moduleNames && item.moduleNames.length > 0) {
          t16 = item.moduleNames.find(m => enabled?.includes(m)) ?? item.moduleNames[0];
          break bb0;
        }
      }
      t16 = undefined;
    }
    $[51] = currentRoute;
    $[52] = tenant?.enabledModules;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  const activeModule = t16;
  const lastExpandedRoute = useRef(undefined);
  let t17;
  let t18;
  if ($[54] !== currentRoute || $[55] !== expandSectionsForRoute) {
    t17 = () => {
      if (currentRoute && currentRoute !== lastExpandedRoute.current) {
        lastExpandedRoute.current = currentRoute;
        const breadcrumbPath_0 = getRelativePath(currentRoute);
        const parentSections = breadcrumbPath_0.slice(0, -1).map(_temp4).filter(Boolean);
        if (parentSections.length > 0) {
          expandSectionsForRoute(parentSections);
        }
      }
    };
    t18 = [currentRoute, expandSectionsForRoute];
    $[54] = currentRoute;
    $[55] = expandSectionsForRoute;
    $[56] = t17;
    $[57] = t18;
  } else {
    t17 = $[56];
    t18 = $[57];
  }
  useEffect(t17, t18);
  const isMac = typeof navigator !== "undefined" && (navigator.userAgent?.includes("Mac") || /Mac|iPod|iPhone|iPad/.test(navigator.userAgent));
  const shortcut = isMac ? "\u2318S" : "Ctrl+S";
  let t19;
  if ($[58] !== isHoverSidebarOpen || $[59] !== isMobile || $[60] !== isSidebarCollapsed || $[61] !== setIsHoverSidebarOpen || $[62] !== setIsSidebarCollapsed) {
    t19 = () => {
      if (isMobile) {
        setIsSidebarCollapsed(true);
        setIsHoverSidebarOpen(!isHoverSidebarOpen);
      } else {
        setIsSidebarCollapsed(!isSidebarCollapsed);
      }
    };
    $[58] = isHoverSidebarOpen;
    $[59] = isMobile;
    $[60] = isSidebarCollapsed;
    $[61] = setIsHoverSidebarOpen;
    $[62] = setIsSidebarCollapsed;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  const toggleSidebar = t19;
  const t20 = !isSidebarCollapsed;
  let t21;
  if ($[64] !== setIsSidebarCollapsed) {
    t21 = open => setIsSidebarCollapsed(!open);
    $[64] = setIsSidebarCollapsed;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  let t22;
  if ($[66] !== isSidebarCollapsed || $[67] !== setIsHoverSidebarOpen) {
    t22 = <div className="absolute bottom-0 left-0 top-14 z-40 hidden w-4 md:block" onMouseEnter={() => {
      if (isSidebarCollapsed) {
        setIsHoverSidebarOpen(true);
      }
    }} />;
    $[66] = isSidebarCollapsed;
    $[67] = setIsHoverSidebarOpen;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  const t23 = isHoverSidebarOpen && isSidebarCollapsed ? "pointer-events-auto bg-background/50 backdrop-blur-sm md:bg-transparent md:backdrop-blur-none z-50" : "pointer-events-none bg-transparent backdrop-blur-0 md:bg-transparent md:backdrop-blur-none";
  let t24;
  if ($[69] !== t23) {
    t24 = cx("absolute inset-y-0 left-0 right-0 z-10 h-dvh transition-all duration-200 ease-in-out md:w-72 md:pr-4", t23);
    $[69] = t23;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  let t25;
  let t26;
  if ($[71] !== isSidebarCollapsed || $[72] !== setIsHoverSidebarOpen) {
    t25 = event_0 => {
      if (isSidebarCollapsed && event_0.target === event_0.currentTarget) {
        setIsHoverSidebarOpen(false);
      }
    };
    t26 = () => {
      if (isSidebarCollapsed) {
        setIsHoverSidebarOpen(false);
      }
    };
    $[71] = isSidebarCollapsed;
    $[72] = setIsHoverSidebarOpen;
    $[73] = t25;
    $[74] = t26;
  } else {
    t25 = $[73];
    t26 = $[74];
  }
  const t27 = isHoverSidebarOpen && isSidebarCollapsed ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0";
  let t28;
  if ($[75] !== t27) {
    t28 = cx("z-20 w-3/4 transform-gpu transition-all duration-200 ease-in-out md:w-64", t27);
    $[75] = t27;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== getBackRouteInfo) {
    t29 = getBackRouteInfo();
    $[77] = getBackRouteInfo;
    $[78] = t29;
  } else {
    t29 = $[78];
  }
  const t30 = isSidebarCollapsed || isMobile;
  let t31;
  if ($[79] !== currentRoute || $[80] !== handleBack || $[81] !== isSubNavMode || $[82] !== menuItems || $[83] !== t29.text || $[84] !== t30) {
    t31 = <AppSidebar isSubNavMode={isSubNavMode} handleBack={handleBack} backText={t29.text} menuItems={menuItems} currentRoute={currentRoute} tourAnchors={t30} className="m-2 h-[calc(100dvh-1rem)] overflow-hidden rounded-lg border border-border bg-background/80 shadow-lg backdrop-blur-sm" />;
    $[79] = currentRoute;
    $[80] = handleBack;
    $[81] = isSubNavMode;
    $[82] = menuItems;
    $[83] = t29.text;
    $[84] = t30;
    $[85] = t31;
  } else {
    t31 = $[85];
  }
  let t32;
  if ($[86] !== t28 || $[87] !== t31) {
    t32 = <div className={t28}>{t31}</div>;
    $[86] = t28;
    $[87] = t31;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  let t33;
  if ($[89] !== t24 || $[90] !== t25 || $[91] !== t26 || $[92] !== t32) {
    t33 = <div className={t24} onClick={t25} onMouseLeave={t26}>{t32}</div>;
    $[89] = t24;
    $[90] = t25;
    $[91] = t26;
    $[92] = t32;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  const t34 = isSidebarCollapsed || isMobile ? "w-0 p-0" : "w-[16.5rem] p-2 pr-0";
  let t35;
  if ($[94] !== t34) {
    t35 = cx("relative z-50 flex h-full transition-all duration-300 sidebar", t34);
    $[94] = t34;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  const t36 = isSidebarCollapsed ? "-translate-x-2 scale-[0.98] opacity-0" : "translate-x-0 scale-100 opacity-100";
  let t37;
  if ($[96] !== t36) {
    t37 = cx("hidden h-full w-full origin-left overflow-hidden rounded-lg bg-background/80 shadow-md backdrop-blur-sm transition-all duration-300 ease-out md:block", t36);
    $[96] = t36;
    $[97] = t37;
  } else {
    t37 = $[97];
  }
  let t38;
  if ($[98] !== getBackRouteInfo) {
    t38 = getBackRouteInfo();
    $[98] = getBackRouteInfo;
    $[99] = t38;
  } else {
    t38 = $[99];
  }
  const t39 = !isSidebarCollapsed && !isMobile;
  let t40;
  if ($[100] !== currentRoute || $[101] !== handleBack || $[102] !== isSubNavMode || $[103] !== menuItems || $[104] !== t38.text || $[105] !== t39) {
    t40 = <AppSidebar isSubNavMode={isSubNavMode} handleBack={handleBack} backText={t38.text} menuItems={menuItems} currentRoute={currentRoute} tourAnchors={t39} className="h-full" />;
    $[100] = currentRoute;
    $[101] = handleBack;
    $[102] = isSubNavMode;
    $[103] = menuItems;
    $[104] = t38.text;
    $[105] = t39;
    $[106] = t40;
  } else {
    t40 = $[106];
  }
  let t41;
  if ($[107] !== t37 || $[108] !== t40) {
    t41 = <div className={t37}>{t40}</div>;
    $[107] = t37;
    $[108] = t40;
    $[109] = t41;
  } else {
    t41 = $[109];
  }
  const t42 = isNotificationsPanelOpen ? "translate-x-0 w-80" : "-translate-x-2 border-0 w-0";
  const t43 = isSidebarCollapsed && isNotificationsPanelOpen && "!left-0 !ml-2";
  let t44;
  if ($[110] !== t42 || $[111] !== t43) {
    t44 = cx("absolute left-full top-2 ml-2 hidden h-[calc(100%-1rem)] transform-gpu", "origin-left overflow-hidden rounded-lg border border-border", "bg-background/80 shadow-lg backdrop-blur-sm transition-all", "duration-200 ease-out md:block", t42, t43);
    $[110] = t42;
    $[111] = t43;
    $[112] = t44;
  } else {
    t44 = $[112];
  }
  let t45;
  if ($[113] !== isNotificationsPanelOpen) {
    t45 = isNotificationsPanelOpen && <NotificationsPanel className="h-full w-80" />;
    $[113] = isNotificationsPanelOpen;
    $[114] = t45;
  } else {
    t45 = $[114];
  }
  let t46;
  if ($[115] !== t44 || $[116] !== t45) {
    t46 = <div ref={notificationsPanelRef} className={t44}>{t45}</div>;
    $[115] = t44;
    $[116] = t45;
    $[117] = t46;
  } else {
    t46 = $[117];
  }
  const t47 = isPatientStatusPanelOpen ? "translate-x-0 w-80" : "-translate-x-2 border-0 w-0";
  const t48 = isSidebarCollapsed && isPatientStatusPanelOpen && "!left-0 !ml-2";
  let t49;
  if ($[118] !== t47 || $[119] !== t48) {
    t49 = cx("absolute left-full top-2 ml-2 hidden h-[calc(100%-1rem)]", "origin-left overflow-hidden rounded-lg border border-border", "bg-background/80 shadow-lg backdrop-blur-sm transition-all", "duration-200 ease-out md:block", t47, t48);
    $[118] = t47;
    $[119] = t48;
    $[120] = t49;
  } else {
    t49 = $[120];
  }
  let t50;
  if ($[121] !== isPatientStatusPanelOpen) {
    t50 = isPatientStatusPanelOpen && <PatientStatusPanel className="h-full w-80" />;
    $[121] = isPatientStatusPanelOpen;
    $[122] = t50;
  } else {
    t50 = $[122];
  }
  let t51;
  if ($[123] !== t49 || $[124] !== t50) {
    t51 = <div ref={patientStatusPanelRef} className={t49}>{t50}</div>;
    $[123] = t49;
    $[124] = t50;
    $[125] = t51;
  } else {
    t51 = $[125];
  }
  const t52 = isTasksPanelOpen ? "translate-x-0 w-80" : "-translate-x-2 border-0 w-0";
  const t53 = isSidebarCollapsed && isTasksPanelOpen && "!left-0 !ml-2";
  let t54;
  if ($[126] !== t52 || $[127] !== t53) {
    t54 = cx("absolute left-full top-2 ml-2 hidden h-[calc(100%-1rem)]", "origin-left overflow-hidden rounded-lg border border-border", "bg-background/80 shadow-lg backdrop-blur-sm transition-all", "duration-200 ease-out md:block", t52, t53);
    $[126] = t52;
    $[127] = t53;
    $[128] = t54;
  } else {
    t54 = $[128];
  }
  let t55;
  if ($[129] !== isTasksPanelOpen) {
    t55 = isTasksPanelOpen && <TasksPanel className="h-full w-80" />;
    $[129] = isTasksPanelOpen;
    $[130] = t55;
  } else {
    t55 = $[130];
  }
  let t56;
  if ($[131] !== t54 || $[132] !== t55) {
    t56 = <div ref={tasksPanelRef} className={t54}>{t55}</div>;
    $[131] = t54;
    $[132] = t55;
    $[133] = t56;
  } else {
    t56 = $[133];
  }
  let t57;
  if ($[134] !== t35 || $[135] !== t41 || $[136] !== t46 || $[137] !== t51 || $[138] !== t56) {
    t57 = <div ref={sidebarRef} className={t35}>{t41}{t46}{t51}{t56}</div>;
    $[134] = t35;
    $[135] = t41;
    $[136] = t46;
    $[137] = t51;
    $[138] = t56;
    $[139] = t57;
  } else {
    t57 = $[139];
  }
  const t58 = isHoverSidebarOpen && isMobile && "scale-95 transition-transform duration-200 ease-in-out";
  const t59 = (isNotificationsPanelOpen && isNotificationsPanelLocked || isPatientStatusPanelOpen && isPatientStatusPanelLocked || isTasksPanelOpen && isTasksPanelLocked) && "md:ml-[20.5rem]";
  const t60 = isWhisperPanelOpen && isWhisperPanelLocked && "md:mr-[44rem]";
  let t61;
  if ($[140] !== t58 || $[141] !== t59 || $[142] !== t60) {
    t61 = cx("flex h-full min-w-0 flex-1 flex-col transition-[margin] duration-200 relative", t58, "max-w-full", t59, t60);
    $[140] = t58;
    $[141] = t59;
    $[142] = t60;
    $[143] = t61;
  } else {
    t61 = $[143];
  }
  const t62 = isSidebarCollapsed ? "rounded-none md:rounded-lg" : "md:rounded-lg";
  let t63;
  if ($[144] !== t62) {
    t63 = cx("flex h-full flex-col overflow-auto md:border md:border-border bg-background/80 shadow-md", t62);
    $[144] = t62;
    $[145] = t63;
  } else {
    t63 = $[145];
  }
  let t64;
  if ($[146] !== breadcrumbPath || $[147] !== closeWhisperPanel || $[148] !== currentRoute || $[149] !== currentUser || $[150] !== editorHeight || $[151] !== getBackRouteInfo || $[152] !== globalActionConfig || $[153] !== handleBack || $[154] !== isMobile || $[155] !== isSidebarCollapsed || $[156] !== isSubNavMode || $[157] !== isWhisperEnabled || $[158] !== isWhisperPanelOpen || $[159] !== locations || $[160] !== mobileNotificationsOpen || $[161] !== openWhisperPanel || $[162] !== tenant?.name || $[163] !== toggleSidebar || $[164] !== totalTeamUnread || $[165] !== unreadCount) {
    t64 = breadcrumbPath.length > 0 && <div className={cx("flex flex-row items-center gap-1.5 md:gap-0 md:h-10", "border-b border-border shadow-md", "flex-shrink-0", "bg-background/20 backdrop-blur-sm", "fixed md:static", "transition-all duration-300 ease-in-out", "bottom-12", "rounded-full md:rounded-none md:shadow-none md:backdrop-blur-none", "z-50 md:z-[unset]", "left-[50vw] -translate-x-1/2", "md:-translate-x-0", (!isSidebarCollapsed || !isSubNavMode) && "md:pl-1", currentRoute === "/billing/check-out" && "bottom-20", currentRoute === "/comms/chat" && "bottom-20", currentRoute === "/ask-decoda" && "bottom-20")} style={isMobile && currentRoute === "/comms/chat" ? {
      bottom: `calc(3rem + ${editorHeight}px)`
    } : undefined}><div className="relative shrink-0"><button onClick={toggleSidebar} className="flex size-9 items-center justify-center rounded-full bg-background/90 text-foreground-secondary shadow-md backdrop-blur-sm hover:text-foreground md:bg-transparent md:!shadow-none md:backdrop-blur-none md:rounded-md" id="sidebar-toggle" data-ph-capture-attribute-id="sidebar-toggle" aria-label={isSidebarCollapsed ? `Expand sidebar (${shortcut})` : `Collapse sidebar (${shortcut})`} title={isSidebarCollapsed ? `Expand sidebar (${shortcut})` : `Collapse sidebar (${shortcut})`}><BiMenu className="size-5" /></button>{isSidebarCollapsed && unreadCount > 0 && <span className="pointer-events-none absolute right-0.5 top-1 hidden min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-tiny font-medium leading-4 text-destructive-foreground md:flex">{unreadCount > 99 ? "99+" : unreadCount}</span>}</div>{isSubNavMode && isSidebarCollapsed && !isMobile && <Button onClick={handleBack} variant="ghost" size="sm" className="group/return-button h-full w-fit gap-1 text-nowrap text-foreground-secondary" icon={<HiArrowLeft className="h-4 w-0 transition-all duration-200 ease-in-out group-hover/return-button:block group-hover/return-button:w-4" />}>{getBackRouteInfo().text}</Button>}{globalActionConfig && <div className="rounded-full bg-background/90 shadow-md backdrop-blur-sm"><Button variant="secondary" {...globalActionConfig as ButtonProps} className={cx("rounded-full shadow-md !h-9", globalActionConfig.className)} /></div>}<CommandBar size={globalActionConfig ? "sm" : "base"} className={cx("rounded-full bg-background/90 text-foreground-secondary shadow-md backdrop-blur-sm md:hidden", "border-none", globalActionConfig ? "aspect-square justify-center" : "px-3")} /><Popover open={mobileNotificationsOpen} onOpenChange={setMobileNotificationsOpen}><PopoverTrigger asChild={true}><button className="relative aspect-square rounded-full bg-background/90 text-foreground-secondary shadow-md backdrop-blur-sm md:hidden flex size-9 items-center justify-center" aria-label="Notifications"><HiBell className="size-4" />{unreadCount > 0 && <span className="pointer-events-none absolute right-0 top-0.5 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-tiny font-medium leading-4 text-destructive-foreground flex">{unreadCount > 99 ? "99+" : unreadCount}</span>}</button></PopoverTrigger><PopoverContent side="top" align="center" className="w-[calc(100vw-2rem)] max-w-sm h-[70vh] p-0 overflow-hidden"><NotificationsPanel className="h-full" mode="mobile" onClose={() => setMobileNotificationsOpen(false)} /></PopoverContent></Popover>{isWhisperEnabled && <Popover open={isMobile && isWhisperPanelOpen} onOpenChange={open_0 => open_0 ? openWhisperPanel() : closeWhisperPanel()}><PopoverTrigger asChild={true}><button className="relative aspect-square rounded-full bg-background/90 text-foreground-secondary shadow-md backdrop-blur-sm md:hidden flex size-9 items-center justify-center" aria-label="Whisper"><RiMessage3Line className="size-4" />{totalTeamUnread > 0 && <span className="pointer-events-none absolute right-0 top-0.5 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-tiny font-medium leading-4 text-destructive-foreground flex">{totalTeamUnread > 99 ? "99+" : totalTeamUnread}</span>}</button></PopoverTrigger><PopoverContent side="top" align="center" className="w-[calc(100vw-2rem)] max-w-sm h-[70vh] p-0 overflow-hidden"><TeamChatPanel className="h-full" mode="mobile" onClose={closeWhisperPanel} /></PopoverContent></Popover>}<ProfileButton><Button variant="ghost" className="aspect-square rounded-full bg-background/90 text-foreground-secondary shadow-md backdrop-blur-sm md:hidden" icon={<FaUser className="size-4" />} /></ProfileButton>{!isMobile && <BreadcrumbTrail path={breadcrumbPath} currentRoute={currentRoute} />}{isSidebarCollapsed && <span className="hidden min-w-0 flex-1 truncate pl-2.5 text-center text-caption-emphasis text-foreground-muted md:mr-2.5 md:block">{tenant?.name}{" "}{currentUser?.primaryLocationId && locations.length > 1 && <span>{`/ ${locations.find(location => location.id === currentUser.primaryLocationId)?.name}`}</span>}</span>}<div className="hidden md:flex ml-auto"><DeskPhoneCallIndicator /><AskDecodaHeaderButton />{isWhisperEnabled && <WhisperHeaderButton />}<ClockInButton className="hidden shrink-0 items-center md:flex" /></div></div>;
    $[146] = breadcrumbPath;
    $[147] = closeWhisperPanel;
    $[148] = currentRoute;
    $[149] = currentUser;
    $[150] = editorHeight;
    $[151] = getBackRouteInfo;
    $[152] = globalActionConfig;
    $[153] = handleBack;
    $[154] = isMobile;
    $[155] = isSidebarCollapsed;
    $[156] = isSubNavMode;
    $[157] = isWhisperEnabled;
    $[158] = isWhisperPanelOpen;
    $[159] = locations;
    $[160] = mobileNotificationsOpen;
    $[161] = openWhisperPanel;
    $[162] = tenant?.name;
    $[163] = toggleSidebar;
    $[164] = totalTeamUnread;
    $[165] = unreadCount;
    $[166] = t64;
  } else {
    t64 = $[166];
  }
  let t65;
  if ($[167] !== activeModule || $[168] !== children || $[169] !== tenant?.enabledModules) {
    t65 = activeModule && !tenant?.enabledModules?.includes(activeModule) ? <MissingDataPage module={activeModule} /> : children;
    $[167] = activeModule;
    $[168] = children;
    $[169] = tenant?.enabledModules;
    $[170] = t65;
  } else {
    t65 = $[170];
  }
  let t66;
  if ($[171] !== t63 || $[172] !== t64 || $[173] !== t65) {
    t66 = <div className="flex h-full flex-col p-0 shadow-border/50 md:p-2 md:shadow-inner"><main className={t63}>{t64}{t65}</main></div>;
    $[171] = t63;
    $[172] = t64;
    $[173] = t65;
    $[174] = t66;
  } else {
    t66 = $[174];
  }
  let t67;
  if ($[175] !== t61 || $[176] !== t66) {
    t67 = <div ref={setMainContentElement} className={t61}>{t66}</div>;
    $[175] = t61;
    $[176] = t66;
    $[177] = t67;
  } else {
    t67 = $[177];
  }
  let t68;
  if ($[178] !== mainContentElement || $[179] !== t67) {
    t68 = <DrawerPortalContainerContext.Provider value={mainContentElement}>{t67}</DrawerPortalContainerContext.Provider>;
    $[178] = mainContentElement;
    $[179] = t67;
    $[180] = t68;
  } else {
    t68 = $[180];
  }
  let t69;
  if ($[181] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = <RightSidebar />;
    $[181] = t69;
  } else {
    t69 = $[181];
  }
  let t70;
  if ($[182] !== closePatientStatusPanel || $[183] !== isMobile || $[184] !== isPatientStatusPanelOpen) {
    t70 = isMobile && isPatientStatusPanelOpen && <div className="fixed inset-0 z-50 flex bg-background md:hidden"><PatientStatusPanel className="h-full w-full" mode="mobile" onClose={closePatientStatusPanel} /></div>;
    $[182] = closePatientStatusPanel;
    $[183] = isMobile;
    $[184] = isPatientStatusPanelOpen;
    $[185] = t70;
  } else {
    t70 = $[185];
  }
  let t71;
  if ($[186] !== closeTasksPanel || $[187] !== isMobile || $[188] !== isTasksPanelOpen) {
    t71 = isMobile && isTasksPanelOpen && <div className="fixed inset-0 z-50 flex bg-background md:hidden"><TasksPanel className="h-full w-full" mode="mobile" onClose={closeTasksPanel} /></div>;
    $[186] = closeTasksPanel;
    $[187] = isMobile;
    $[188] = isTasksPanelOpen;
    $[189] = t71;
  } else {
    t71 = $[189];
  }
  let t72;
  if ($[190] !== isWhisperEnabled || $[191] !== isWhisperPanelOpen) {
    t72 = isWhisperEnabled && <div ref={whisperPanelRef} className={cx("fixed right-2 top-2 z-30 hidden h-[calc(100%-1rem)] md:block", "origin-right overflow-hidden rounded-lg border border-border", "bg-background/80 shadow-lg backdrop-blur-sm transition-all", "duration-200 ease-out", isWhisperPanelOpen ? "w-[700px] translate-x-0" : "w-0 translate-x-2 border-0")}>{isWhisperPanelOpen && <TeamChatPanel className="h-full w-[700px]" />}</div>;
    $[190] = isWhisperEnabled;
    $[191] = isWhisperPanelOpen;
    $[192] = t72;
  } else {
    t72 = $[192];
  }
  let t73;
  if ($[193] !== t22 || $[194] !== t33 || $[195] !== t57 || $[196] !== t68 || $[197] !== t70 || $[198] !== t71 || $[199] !== t72) {
    t73 = <div className="z-10 flex h-full w-full">{t22}{t33}{t57}{t68}{t69}{t70}{t71}{t72}</div>;
    $[193] = t22;
    $[194] = t33;
    $[195] = t57;
    $[196] = t68;
    $[197] = t70;
    $[198] = t71;
    $[199] = t72;
    $[200] = t73;
  } else {
    t73 = $[200];
  }
  let t74;
  if ($[201] !== t20 || $[202] !== t21 || $[203] !== t73) {
    t74 = <><SidebarProvider open={t20} onOpenChange={t21}>{t73}</SidebarProvider></>;
    $[201] = t20;
    $[202] = t21;
    $[203] = t73;
    $[204] = t74;
  } else {
    t74 = $[204];
  }
  return t74;
}
function _temp4(item_0) {
  return item_0.name;
}
function _temp3(locked) {
  return !locked;
}
function _temp2(dialog) {
  return dialog.querySelector("[aria-expanded=\"true\"]");
}
function _temp(sum, c) {
  return sum + c;
}